import { useState, useEffect, useCallback } from 'react';
import { ShoppingBag, X } from 'lucide-react';
import { api } from '../lib/api';

// Sample data outside component to avoid recreating on each render
const sampleProducts = [
  "Obsidian Void Skull",
  "Ethereal Galaxy Skull",
  "Forest Spirit Skull",
  "Midnight Blood Skull",
  "Dragon Scale Skull",
  "Ocean Abyss Skull",
  "Solar Skullz"
];

const sampleLocations = [
  "California", "Texas", "New York", "Florida", "Ohio",
  "Georgia", "Arizona", "Colorado", "Washington", "Oregon"
];

const PurchaseNotification = () => {
  const [notification, setNotification] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isEnabled, setIsEnabled] = useState(true);

  // Generate a random notification
  const generateNotification = useCallback(() => {
    const product = sampleProducts[Math.floor(Math.random() * sampleProducts.length)];
    const location = sampleLocations[Math.floor(Math.random() * sampleLocations.length)];
    const timeAgo = Math.floor(Math.random() * 10) + 1;
    
    return {
      product,
      location,
      timeAgo: `${timeAgo} minute${timeAgo > 1 ? 's' : ''} ago`
    };
  }, []);

  // Check if notifications are enabled
  useEffect(() => {
    const checkSetting = async () => {
      try {
        const result = await api.getPurchaseNotificationSetting();
        setIsEnabled(result.enabled);
      } catch (error) {
        // Default to enabled if can't fetch setting
        setIsEnabled(true);
      }
    };
    checkSetting();
    
    // Re-check every 30 seconds in case admin changes the setting
    const interval = setInterval(checkSetting, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!isEnabled) return;

    // Initial delay before first notification (5-15 seconds)
    const initialDelay = Math.random() * 10000 + 5000;
    
    const showNotification = () => {
      const newNotification = generateNotification();
      setNotification(newNotification);
      setIsVisible(true);
      
      // Hide after 5 seconds
      setTimeout(() => {
        setIsVisible(false);
      }, 5000);
    };

    // First notification after initial delay
    const initialTimer = setTimeout(() => {
      showNotification();
      
      // Then show notifications every 30-60 seconds
      const interval = setInterval(() => {
        showNotification();
      }, Math.random() * 30000 + 30000);
      
      return () => clearInterval(interval);
    }, initialDelay);

    return () => clearTimeout(initialTimer);
  }, [isEnabled, generateNotification]);

  const handleClose = () => {
    setIsVisible(false);
  };

  if (!isEnabled || !isVisible || !notification) return null;

  return (
    <div 
      data-testid="purchase-notification"
      className={`fixed bottom-6 left-6 z-50 max-w-sm transform transition-all duration-500 ease-out ${
        isVisible ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0'
      }`}
    >
      <div className="bg-void-paper border border-cyan-400/30 shadow-lg shadow-cyan-400/10 p-4 flex items-start gap-4">
        {/* Icon */}
        <div className="w-10 h-10 bg-cyan-400/20 rounded-full flex items-center justify-center flex-shrink-0">
          <ShoppingBag className="w-5 h-5 text-cyan-400" />
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-medium">
            Someone from <span className="text-cyan-400">{notification.location}</span> just purchased
          </p>
          <p className="text-neutral-300 text-sm font-semibold truncate mt-1">
            {notification.product}
          </p>
          <p className="text-neutral-500 text-xs mt-1">
            {notification.timeAgo}
          </p>
        </div>
        
        {/* Close button */}
        <button 
          onClick={handleClose}
          className="text-neutral-500 hover:text-white transition-colors"
          aria-label="Close notification"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default PurchaseNotification;
